// =============================================================
// TournamentSection — Firestore + Firebase Storage
// =============================================================
// • Tournaments stored at /tournaments/{tournamentId} with userId field
//   so the leaderboard can aggregate across all users (rules permit
//   any authenticated user to read all tournaments).
// • Photos stored at /tournaments/{uid}/{tournamentId}.{ext}
// • Leaderboard is computed client-side from the tournaments collection.
// =============================================================

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, deleteObject } from "firebase/storage";
import {
  Medal,
  Trophy,
  Upload,
  Trash2,
  ImageIcon,
  Crown,
  Plus,
} from "lucide-react";
import AuthImage from "./AuthImage";
import { db, storage } from "../lib/firebase";

const PLACEMENTS = [
  { key: "Gold", color: "bg-yellow-500 text-black", border: "border-l-yellow-500" },
  { key: "Silver", color: "bg-zinc-300 text-black", border: "border-l-zinc-300" },
  { key: "Bronze", color: "bg-amber-700 text-white", border: "border-l-amber-700" },
  { key: "No medal", color: "bg-zinc-800 text-zinc-300", border: "border-l-zinc-700" },
];

const RANK_COLOR = ["text-yellow-500", "text-zinc-300", "text-amber-600"];
const DEFAULT_RANK_COLOR = "text-zinc-600";
const rankColor = (idx) => RANK_COLOR[idx] ?? DEFAULT_RANK_COLOR;

const EMPTY_FORM = {
  name: "",
  date: "",
  placement: "Gold",
  division: "",
  weight_class: "",
  submission: "",
  notes: "",
};

export default function TournamentSection({ belt, user }) {
  const [myTournaments, setMyTournaments] = useState([]);
  const [allTournaments, setAllTournaments] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Subscribe to ALL tournaments (for leaderboard) + filter mine client-side
  useEffect(() => {
    if (!user?.uid) return undefined;
    const q = query(collection(db, "tournaments"), orderBy("date", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllTournaments(rows);
        setMyTournaments(rows.filter((t) => t.userId === user.uid));
      },
      (err) => console.error("Tournaments subscribe failed", err)
    );
    return unsub;
  }, [user?.uid]);

  // Compute leaderboard from allTournaments
  const leaderboard = useMemo(() => {
    const byUser = new Map();
    for (const t of allTournaments) {
      const entry = byUser.get(t.userId) || {
        userId: t.userId,
        userName: t.userName,
        gold: 0,
        silver: 0,
        bronze: 0,
        no_medal: 0,
        total: 0,
        latestBelt: t.belt,
      };
      entry.userName = t.userName || entry.userName;
      entry.latestBelt = t.belt || entry.latestBelt;
      if (t.placement === "Gold") entry.gold += 1;
      else if (t.placement === "Silver") entry.silver += 1;
      else if (t.placement === "Bronze") entry.bronze += 1;
      else if (t.placement === "No medal") entry.no_medal += 1;
      entry.total += 1;
      byUser.set(t.userId, entry);
    }
    return Array.from(byUser.values())
      .sort(
        (a, b) =>
          b.gold - a.gold ||
          b.silver - a.silver ||
          b.bronze - a.bronze ||
          b.total - a.total
      )
      .slice(0, 20);
  }, [allTournaments]);

  function onPhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) {
      setPhoto(null);
      setPhotoPreview(null);
      return;
    }
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Photo must be JPEG, PNG, or WEBP");
      e.target.value = "";
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo must be under 8 MB");
      e.target.value = "";
      return;
    }
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function submit() {
    if (!form.name.trim() || !form.date || !form.placement || !user?.uid) {
      toast.error("Name, date, and placement required");
      return;
    }
    setSubmitting(true);
    try {
      // 1) Create the Firestore doc first to get an ID for the photo path
      const docRef = await addDoc(collection(db, "tournaments"), {
        userId: user.uid,
        userName: user.displayName || user.email?.split("@")[0] || "Grappler",
        name: form.name.trim(),
        date: form.date,
        placement: form.placement,
        division: form.division.trim() || null,
        weight_class: form.weight_class.trim() || null,
        submission: form.submission.trim() || null,
        notes: form.notes.trim() || null,
        belt: belt || "White",
        photoPath: null,
        createdAt: serverTimestamp(),
      });

      // 2) Upload the photo (if provided) and patch the doc with the path
      if (photo) {
        const ext = (photo.name.split(".").pop() || "jpg").toLowerCase();
        const path = `tournaments/${user.uid}/${docRef.id}.${ext}`;
        await uploadBytes(storageRef(storage, path), photo, { contentType: photo.type });
        // Update the doc with the path (use updateDoc via dynamic import to keep imports tight)
        const { updateDoc } = await import("firebase/firestore");
        await updateDoc(docRef, { photoPath: path });
      }

      toast.success("Tournament logged");
      setForm(EMPTY_FORM);
      setPhoto(null);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoPreview(null);
    } catch (err) {
      console.error("Tournament submit failed", err);
      toast.error(err?.message || "Failed to log tournament");
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(t) {
    try {
      if (t.photoPath) {
        try {
          await deleteObject(storageRef(storage, t.photoPath));
        } catch (err) {
          console.warn("Photo delete failed (may not exist)", err);
        }
      }
      await deleteDoc(doc(db, "tournaments", t.id));
      toast.success("Tournament removed");
    } catch (err) {
      console.error("Delete tournament failed", err);
      toast.error("Failed to remove");
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <section className="tactical-card p-6 lg:col-span-2" data-testid="tournaments-section">
        <p className="font-label text-red-600">// 05</p>
        <h2 className="font-heading text-3xl mt-1 flex items-center gap-2">
          <Trophy className="w-6 h-6" strokeWidth={2.5} /> Tournament Results
        </h2>
        <p className="text-zinc-500 text-sm mt-1">
          Document your competition history. Photos go in the global hall of fame.
        </p>

        <div className="border border-zinc-800 bg-[#0A0A0A] p-5 mt-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="font-label block mb-2">Event Name *</label>
              <input
                data-testid="tournament-name-input"
                placeholder="ex: IBJJF Pan Ams 2026"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#141414] border border-zinc-800 px-3 py-2.5 text-sm focus:border-red-600 focus:outline-none"
              />
            </div>
            <div>
              <label className="font-label block mb-2">Date *</label>
              <input
                type="date"
                data-testid="tournament-date-input"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full bg-[#141414] border border-zinc-800 px-3 py-2.5 text-sm focus:border-red-600 focus:outline-none"
              />
            </div>
            <div>
              <label className="font-label block mb-2">Placement *</label>
              <select
                data-testid="tournament-placement-select"
                value={form.placement}
                onChange={(e) => setForm({ ...form, placement: e.target.value })}
                className="w-full bg-[#141414] border border-zinc-800 px-3 py-2.5 text-sm focus:border-red-600 focus:outline-none"
              >
                {PLACEMENTS.map((p) => (
                  <option key={p.key}>{p.key}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-label block mb-2">Division</label>
              <input
                data-testid="tournament-division-input"
                placeholder="ex: Adult / Master 1"
                value={form.division}
                onChange={(e) => setForm({ ...form, division: e.target.value })}
                className="w-full bg-[#141414] border border-zinc-800 px-3 py-2.5 text-sm focus:border-red-600 focus:outline-none"
              />
            </div>
            <div>
              <label className="font-label block mb-2">Weight Class</label>
              <input
                data-testid="tournament-weight-input"
                placeholder="ex: Lightweight"
                value={form.weight_class}
                onChange={(e) => setForm({ ...form, weight_class: e.target.value })}
                className="w-full bg-[#141414] border border-zinc-800 px-3 py-2.5 text-sm focus:border-red-600 focus:outline-none"
              />
            </div>
            <div>
              <label className="font-label block mb-2">Submission / Finish</label>
              <input
                data-testid="tournament-submission-input"
                placeholder="ex: Triangle from guard"
                value={form.submission}
                onChange={(e) => setForm({ ...form, submission: e.target.value })}
                className="w-full bg-[#141414] border border-zinc-800 px-3 py-2.5 text-sm focus:border-red-600 focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="font-label block mb-2">Notes</label>
            <textarea
              data-testid="tournament-notes-input"
              placeholder="Match recap, lessons, plans for next time..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-[#141414] border border-zinc-800 px-3 py-2.5 text-sm min-h-[80px] focus:border-red-600 focus:outline-none resize-y"
            />
          </div>
          <div>
            <label className="font-label block mb-2">Photo (jpg / png / webp · max 8 MB)</label>
            <div className="flex items-center gap-3">
              <label className="flex-1 border border-zinc-800 bg-[#141414] px-3 py-2.5 text-sm cursor-pointer flex items-center gap-2 hover:border-zinc-600 transition-colors">
                <Upload className="w-4 h-4 text-zinc-400" />
                <span className="text-zinc-400">
                  {photo ? photo.name : "Choose photo (optional)"}
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  data-testid="tournament-photo-input"
                  onChange={onPhotoChange}
                  className="hidden"
                />
              </label>
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt="preview"
                  className="w-14 h-14 object-cover border border-zinc-700"
                  data-testid="tournament-photo-preview"
                />
              )}
            </div>
          </div>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="add-tournament-button"
            className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-heading tracking-wider py-3 flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={3} />
            {submitting ? "Logging..." : "Log Tournament"}
          </button>
        </div>

        <div className="mt-5 space-y-3" data-testid="tournaments-list">
          {myTournaments.length === 0 && (
            <div className="border border-dashed border-zinc-800 py-10 text-center">
              <p className="font-label text-zinc-600">No tournaments logged yet</p>
            </div>
          )}
          {myTournaments.map((t) => {
            const placement = PLACEMENTS.find((p) => p.key === t.placement) || PLACEMENTS[3];
            return (
              <div
                key={t.id}
                data-testid={`tournament-item-${t.id}`}
                className={`border border-zinc-800 bg-[#0A0A0A] p-4 border-l-4 ${placement.border}`}
              >
                <div className="flex items-start gap-4">
                  {t.photoPath ? (
                    <AuthImage
                      path={t.photoPath}
                      alt={t.name}
                      className="w-20 h-20 object-cover border border-zinc-700 flex-shrink-0"
                      fallback={
                        <div className="w-20 h-20 border border-zinc-800 bg-[#141414] flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-zinc-700" />
                        </div>
                      }
                    />
                  ) : (
                    <div className="w-20 h-20 border border-zinc-800 bg-[#141414] flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-5 h-5 text-zinc-700" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-heading text-lg break-words leading-tight">{t.name}</h3>
                        <p className="font-mono text-xs text-zinc-500 mt-1">{t.date}</p>
                      </div>
                      <span className={`${placement.color} px-2 py-1 text-[10px] font-heading tracking-wider flex items-center gap-1`}>
                        <Medal className="w-3 h-3" strokeWidth={2.5} /> {t.placement.toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {t.division && <span className="border border-zinc-800 px-2 py-0.5 text-[10px] font-mono uppercase text-zinc-300">{t.division}</span>}
                      {t.weight_class && <span className="border border-zinc-800 px-2 py-0.5 text-[10px] font-mono uppercase text-zinc-300">{t.weight_class}</span>}
                      {t.submission && <span className="border border-red-900 bg-red-950/40 px-2 py-0.5 text-[10px] font-mono uppercase text-red-300">{t.submission}</span>}
                    </div>
                    {t.notes && <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{t.notes}</p>}
                  </div>
                  <button
                    onClick={() => remove(t)}
                    data-testid={`delete-tournament-${t.id}`}
                    className="text-zinc-500 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="tactical-card p-6" data-testid="leaderboard-section">
        <p className="font-label text-red-600">// 06</p>
        <h2 className="font-heading text-3xl mt-1 flex items-center gap-2">
          <Crown className="w-6 h-6" strokeWidth={2.5} /> Hall of Fame
        </h2>
        <p className="text-zinc-500 text-sm mt-1">Top grapplers across Tatami Log, ranked by medals.</p>

        <div className="mt-5 space-y-2" data-testid="leaderboard-list">
          {leaderboard.length === 0 && (
            <div className="border border-dashed border-zinc-800 py-10 text-center">
              <p className="font-label text-zinc-600">No tournaments logged yet</p>
            </div>
          )}
          {leaderboard.map((row, idx) => {
            const isMe = user && row.userId === user.uid;
            return (
              <div
                key={row.userId}
                data-testid={`leaderboard-row-${idx + 1}`}
                className={`flex items-center gap-3 border ${
                  isMe ? "border-red-600 bg-red-950/20" : "border-zinc-800 bg-[#0A0A0A]"
                } px-3 py-2.5`}
              >
                <span className={`font-heading text-xl w-8 text-center ${rankColor(idx)}`}>
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-sm tracking-tight truncate">
                    {row.userName}
                    {isMe && <span className="ml-2 text-[10px] text-red-500 font-mono">YOU</span>}
                  </p>
                  <p className="font-mono text-[10px] text-zinc-500">
                    {row.latestBelt || "White"} belt · {row.total} event{row.total === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[10px] font-mono">
                  <span className="text-yellow-500" title="Gold">🥇{row.gold}</span>
                  <span className="text-zinc-300" title="Silver">🥈{row.silver}</span>
                  <span className="text-amber-600" title="Bronze">🥉{row.bronze}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
